import clsx from 'clsx';
import {
  forwardRef,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { SINGLE_VALUE_ARIA_LABEL } from './defaults.js';
import { useSingleValueConfig } from './hooks/use-single-value-config.js';
import { container } from './SingleValue.sty.js';
import { Sparkline } from './slots/Sparkline.js';
import { Toolbar } from './slots/Toolbar.js';
import { Trend } from './slots/Trend.js';
import type { SingleValueRef } from './types/single-value-ref.js';
import type { SingleValueProps } from './types/single-value.js';
import { iterateSlots } from './utils/iterate-slots.js';
import { normalizeConfig } from './utils/single-value-config.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { SingleValueRenderer } from '../core/components/single-value-renderer/SingleValueRenderer.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import { ChartType } from '../core/types/chart-types.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';

const COMPONENT_NAME = 'SingleValue';

/**
 * Single Value component
 */
const SingleValueRoot = /* @__PURE__ */ forwardRef<
  SingleValueRef,
  PropsWithChildren<SingleValueProps>
>((props, forwardedRef) => {
  const {
    data,
    prefixIcon,
    children,
    loading = false,
    width,
    height,
    style: customerStyle,
    className: customerClassNames,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId,
    applyThresholdBackground,
    ...restProps
  } = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);

  const { slots, sparklineChildren } = iterateSlots(children);

  const { emptyState, errorState } = getChartStatesTemplates(children);

  const config = useSingleValueConfig(restProps, slots);

  const sparklineProps = slots.sparkline
    ? {
        data: slots.sparkline?.data ?? [],
        ...config.sparkline,
      }
    : undefined;
  const singleValueRef = useRef<HTMLDivElement>(null);

  const ref = useMergeRefs<SingleValueRef | HTMLDivElement>([
    forwardedRef,
    singleValueRef,
  ]);

  useImperativeHandle(forwardedRef, () => ({
    getConfig: () => {
      return JSON.stringify({
        type: ChartType.SingleValue,
        ...normalizeConfig(config),
      });
    },
  }));

  const chartWidth = coerceSizeValue(width);
  const chartHeight = coerceSizeValue(height);

  const mergedStyle = {
    ...customerStyle,
    ...(chartWidth && { width: chartWidth }),
    ...(chartHeight && { height: chartHeight }),
  };

  return (
    <div
      {...behavioralTrackingProps}
      role="img"
      className={clsx(container, customerClassNames)}
      style={mergedStyle}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-testid={dataTestId}
      data-dt-component={COMPONENT_NAME}
      ref={ref}
      aria-label={SINGLE_VALUE_ARIA_LABEL}
    >
      <SingleValueRenderer
        data={data}
        prefixIcon={prefixIcon}
        {...config}
        sparklineProps={sparklineProps}
        sparklineChildren={sparklineChildren}
        trendProps={slots.trend}
        emptyState={emptyState}
        errorState={errorState}
        loading={loading}
        applyThresholdBackground={applyThresholdBackground}
      >
        {children}
      </SingleValueRenderer>
    </div>
  );
});

(
  SingleValueRoot as typeof SingleValueRoot & { displayName: string }
).displayName = 'SingleValue';

/**
 * The `SingleValue` is a simple and concise representation of a single data point
 * or metric , which can either be a text or a number.
 * @public
 */
export const SingleValue = Object.assign(SingleValueRoot, {
  Sparkline,
  Trend,
  ColorRule,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  Intent,
  Toolbar,
});
